/**
 * ProjectRequirementsPanelNew
 * Combined Requirements & Code Tab — Feature 2 redesign.
 * Merges the former Documents and Executions tabs into a single panel.
 * Layout: Upload → Documents list → ADO work items → Generate action bar → Generation history.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from 'react-bootstrap/Alert';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import Form from 'react-bootstrap/Form';
import
{
    ArrowRepeat,
    CloudUploadFill,
    Download,
    FileEarmarkText,
    Lightning,
    Trash3,
    XLg,
    CheckCircleFill,
    ExclamationTriangleFill,
    ExclamationCircleFill,
    Eye,
} from 'react-bootstrap-icons';

import {
    AdoWorkItem,
    AdoWorkItemsResponse,
    AdoItemNote,
    DocumentClassification,
    RequirementDocument,
    RequirementGenerationJob,
    ReviewRequirementsResponse,
    ReviewIssue,
} from '../../types/requirements';
import { useProjects } from '../../contexts/ProjectContext';
import { useAuth } from '../../contexts/AuthContext';
import { REQUIREMENTS_GENERATE_REVIEW_EVENT } from '../../tour/GuidedTourContext';
import apiClient, { API_ENDPOINTS } from '../../api/config';
import * as RequirementService from '../../api/requirements';
import TestDatasetPreviewModal from '../testdata/TestDatasetPreviewModal';
import StreamingJobLogsModal from '../logs/StreamingJobLogsModal';
import { formatFileSize } from '../../utils/format';
import { formatLocalDateTimeFromUTC } from '../../utils/datetime';
import { filenameFromContentDisposition, triggerDownloadFromBlob } from '../../utils/download';
import { formatApiError } from '../../utils/apiError';
import { normalizeJobType } from '../../utils/jobType';
import { projectTestCasesRoute } from '../../utils/routes';
import './ProjectRequirementsPanel-new.css';

/* ============================================================
   Type definitions
   ============================================================ */

/** Props accepted by the panel */
export type ProjectRequirementsPanelMode = 'full' | 'documents' | 'executions';

interface ProjectRequirementsPanelNewProps
{
    projectId: number;
    /** Kept for backward compatibility — ignored internally; full layout always rendered */
    mode?: ProjectRequirementsPanelMode;
    onSelectJobId?: (jobId: number) => void;
}

interface PanelMessage
{
    type: 'success' | 'danger' | 'info';
    text: string;
}

interface LogsModal
{
    show: boolean;
    title: string;
    jobId?: number;
    status?: string;
}

interface PersistedReviewAnalysisSession
{
    flowId: string;
    status: 'loading' | 'ready';
    readyShown: boolean;
    acknowledged: boolean;
    pendingGeneratePayload: Record<string, any> | null;
    report: ReviewRequirementsResponse | null;
    updatedAt: string;
}

interface RequirementUploadDraft
{
    files: File[];
    fileNotes: Record<string, string>;
    fileClassifications: Record<string, DocumentClassification | ''>;
}

/* ============================================================
   Constants
   ============================================================ */

const ACCEPTED_TYPES = '.txt,.md,.markdown,.csv,.pdf,.docx,.xlsx';
const ACCEPTED_EXTENSIONS = new Set(['.txt', '.md', '.markdown', '.csv', '.pdf', '.docx', '.xlsx']);
const JOB_POLL_INTERVAL_MS = 5000;
const DOCUMENT_POLL_INTERVAL_MS = 2000;
const MAX_HISTORY_DEFAULT = 10;
const MAX_FILES_PER_UPLOAD = 4;
const MAX_UPLOAD_FILE_BYTES = 25 * 1024 * 1024;
const EMPTY_FILE_ERROR = 'Empty files cannot be uploaded. Remove them and try again.';
const REVIEW_ANALYSIS_STORAGE_PREFIX = 'requirements-analysis-session';

const requirementUploadDrafts = new Map<number, RequirementUploadDraft>();

/* Preloaded description templates keyed by classification value */
const DESCRIPTION_TEMPLATES: Record<string, string[]> = {
    new_feature: [
        'This document describes a new feature or capability being added to the product.',
        'This is a requirements spec for a new user journey or workflow not yet present in the system.',
        'This document outlines a new module, service, or integration being introduced.',
    ],
    update_existing: [
        'This document describes changes or improvements to an existing product feature.',
        'This captures updated acceptance criteria or revised requirements for an existing workflow.',
        'This document details a bug fix, enhancement, or change request on existing functionality.',
    ],
};

/* ============================================================
   Helpers
   ============================================================ */

function getDocStatusClass(status: RequirementDocument['status']): string
{
    switch (status)
    {
        case 'ready':      return 'rp-badge--ready';
        case 'extracting': return 'rp-badge--extracting';
        case 'failed':     return 'rp-badge--failed';
        default:           return 'rp-badge--default';
    }
}

function makeReviewAnalysisFlowId(): string
{
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    {
        return crypto.randomUUID();
    }
    return `review-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getReviewAnalysisStorageKey(projectId: number): string
{
    return `${REVIEW_ANALYSIS_STORAGE_PREFIX}:${projectId}`;
}

function readPersistedReviewAnalysisSession(projectId: number): PersistedReviewAnalysisSession | null
{
    if (typeof window === 'undefined' || typeof window.localStorage === 'undefined')
    {
        return null;
    }

    try
    {
        const raw = window.localStorage.getItem(getReviewAnalysisStorageKey(projectId));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as PersistedReviewAnalysisSession;
        if (!parsed || typeof parsed !== 'object' || !parsed.flowId)
        {
            return null;
        }
        return parsed;
    }
    catch
    {
        return null;
    }
}

function writePersistedReviewAnalysisSession(projectId: number, session: PersistedReviewAnalysisSession): void
{
    if (typeof window === 'undefined' || typeof window.localStorage === 'undefined')
    {
        return;
    }

    try
    {
        window.localStorage.setItem(getReviewAnalysisStorageKey(projectId), JSON.stringify(session));
    }
    catch
    {
        /* non-critical */
    }
}

function clearPersistedReviewAnalysisSession(projectId: number): void
{
    if (typeof window === 'undefined' || typeof window.localStorage === 'undefined')
    {
        return;
    }

    try
    {
        window.localStorage.removeItem(getReviewAnalysisStorageKey(projectId));
    }
    catch
    {
        /* non-critical */
    }
}

function readRequirementUploadDraft(projectId: number): RequirementUploadDraft
{
    return requirementUploadDrafts.get(projectId) ?? {
        files: [],
        fileNotes: {},
        fileClassifications: {},
    };
}

function writeRequirementUploadDraft(projectId: number, draft: RequirementUploadDraft): void
{
    if (
        draft.files.length === 0
        && Object.keys(draft.fileNotes).length === 0
        && Object.keys(draft.fileClassifications).length === 0
    )
    {
        requirementUploadDrafts.delete(projectId);
        return;
    }

    requirementUploadDrafts.set(projectId, {
        files: [...draft.files],
        fileNotes: { ...draft.fileNotes },
        fileClassifications: { ...draft.fileClassifications },
    });
}

function clearRequirementUploadDraft(projectId: number): void
{
    requirementUploadDrafts.delete(projectId);
}

function getJobBadgeClass(status: string): string
{
    switch (status.toLowerCase())
    {
        case 'completed':  return 'rp-job-badge--completed';
        case 'running':    return 'rp-job-badge--running';
        case 'queued':     return 'rp-job-badge--pending';
        case 'pending':    return 'rp-job-badge--pending';
        case 'retrying':   return 'rp-job-badge--retrying';
        case 'failed':     return 'rp-job-badge--failed';
        default:           return 'rp-job-badge--default';
    }
}

function isActiveGenerationStatus(status?: string | null): boolean
{
    return ['queued', 'pending', 'running', 'retrying'].includes((status || '').toLowerCase());
}

function jobHasTestCases(job: RequirementGenerationJob): boolean
{
    const explicitCount = Number(job.artifact_counts?.test_cases ?? 0);
    if (explicitCount > 0) return true;
    if (Number(job.live_results?.test_case_count ?? 0) > 0) return true;
    return (job.artifacts ?? []).some(
        (a) => (a.artifact_type ?? '').toLowerCase() === 'test_cases'
    );
}

function renderLiveGenerationResults(job: RequirementGenerationJob): React.ReactNode
{
    const live = job.live_results;
    if (!live) return null;

    const totalStories = Number(live.total_story_count || 0);
    const completedStories = Number(live.completed_story_count || 0);
    const storyPercent = totalStories > 0
        ? Math.min(100, Math.max(0, Math.round((completedStories / totalStories) * 100)))
        : Math.min(100, Math.max(0, Number(job.progress || 0)));
    const previewTests = live.preview_tests ?? [];
    const visibleTests = previewTests.slice(0, 5);
    const latestStory = [...(live.stories ?? [])]
        .sort((a, b) => Number(b.story_index || 0) - Number(a.story_index || 0))[0];

    return (
        <div className="rp-live-results">
            <div className="rp-live-results-header">
                <div className="rp-live-results-title">
                    <span className="rp-history-running-dot" />
                    Saved results are available
                </div>
                <div className="rp-live-results-counts">
                    {completedStories}/{totalStories || '-'} user stories / {live.test_case_count || 0} tests
                </div>
            </div>
            <div className="rp-live-progress-track" aria-label={`Live generation progress ${storyPercent}%`}>
                <div className="rp-live-progress-fill" style={{ width: `${storyPercent}%` }} />
            </div>
            {latestStory && (
                <div className="rp-live-story-line">
                    User Story {latestStory.user_story_id} saved
                    {latestStory.operation_counts && (
                        <span>
                            {' '}/ {latestStory.operation_counts.added || 0} added, {latestStory.operation_counts.updated || 0} updated
                        </span>
                    )}
                </div>
            )}
            {totalStories > 0 && completedStories < totalStories && (
                <div className="rp-live-story-line">
                    Next user story in progress
                </div>
            )}
            {visibleTests.length > 0 && (
                <div className="rp-live-test-list">
                    {visibleTests.map((test, index) => (
                        <div key={`${test.test_key || test.id || index}`} className="rp-live-test-item">
                            <span className="rp-live-test-title">{test.title || test.test_key || `Test ${index + 1}`}</span>
                            {test.user_story_id != null && (
                                <span className="rp-live-test-story">US {test.user_story_id}</span>
                            )}
                        </div>
                    ))}
                    {previewTests.length > visibleTests.length && (
                        <div className="rp-live-test-more">
                            +{previewTests.length - visibleTests.length} more in latest checkpoint
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/* ============================================================
   Component: ProjectRequirementsPanelNew
   ============================================================ */

function ProjectRequirementsPanelNew(
    {
        projectId,
        onSelectJobId
    }: ProjectRequirementsPanelNewProps
): React.ReactElement
{
    const navigate = useNavigate();
    const
    {
        projects,
        selectedProject,
        requirementDocuments,
        requirementJobs,
        fetchRequirementDocuments,
        deleteRequirementDocument,
        reprocessRequirementDocument,
        downloadRequirementDocument,
        fetchRequirementJobs,
        queueRequirementGeneration,
        refreshProject
    } = useProjects();
    const { user } = useAuth();

    /* ---------- Derived data ---------- */
    const currentProject = useMemo(
        () =>
        {
            if (selectedProject && selectedProject.id === projectId) return selectedProject;
            return projects.find((p) => p.id === projectId) ?? null;
        },
        [projects, selectedProject, projectId]
    );

    const isAzureProject = (currentProject?.project_type ?? '').toLowerCase() === 'azure_devops';
    const canViewLogs = user?.can_view_logs ?? true;

    const documents = useMemo(
        () => requirementDocuments[projectId] ?? [],
        [requirementDocuments, projectId]
    );

    const jobs = useMemo(
        () =>
        {
            const list = requirementJobs[projectId] ?? [];
            // Filter out synthetic data jobs so they only show up in the Synthetic Data tab
            return list
                .filter((job) => normalizeJobType((job as any).job_type || job.generation_kind) !== 'synthetic_data')
                .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        },
        [requirementJobs, projectId]
    );

    const hasActiveTestCaseGenerationJob = useMemo(
        () => jobs.some(
            (job) =>
                normalizeJobType((job as any).job_type || job.generation_kind) === 'testcases'
                && isActiveGenerationStatus(job.status)
        ),
        [jobs]
    );

    const readyDocuments = useMemo(
        () => documents.filter((doc) => doc.status === 'ready'),
        [documents]
    );

    const hasProcessingDocuments = useMemo(
        () => documents.some((doc) => doc.status === 'uploaded' || doc.status === 'extracting'),
        [documents]
    );

    /* ---------- Upload state ---------- */
    const initialUploadDraft = readRequirementUploadDraft(projectId);
    const uploadDraftProjectIdRef = useRef(projectId);
    const [selectedFiles, setSelectedFiles] = useState<File[]>(() => initialUploadDraft.files);
    /** Per-file context/role description keyed by file name */
    const [fileNotes, setFileNotes] = useState<Record<string, string>>(() => initialUploadDraft.fileNotes);
    /** Per-file classification keyed by file name */
    const [fileClassifications, setFileClassifications] = useState<Record<string, DocumentClassification | ''>>(
        () => initialUploadDraft.fileClassifications
    );
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    /* ---------- Document selection state ---------- */
    const [selectedDocIds, setSelectedDocIds] = useState<number[]>([]);

    /* ---------- ADO state ---------- */
    // Hydrate from localStorage so previously-fetched items survive navigation
    const adoStorageKey = `ado_items_${projectId}`;
    const [adoWorkItems, setAdoWorkItems] = useState<AdoWorkItem[]>(() =>
    {
        try
        {
            const raw = localStorage.getItem(adoStorageKey);
            return raw ? (JSON.parse(raw) as AdoWorkItem[]) : [];
        }
        catch { return []; }
    });
    const [selectedAdoIds, setSelectedAdoIds] = useState<number[]>(() =>
        adoWorkItems.map((i) => i.id)
    );
    const [adoSyncing, setAdoSyncing] = useState(false);
    const [adoLastSynced, setAdoLastSynced] = useState<string | null>(null);
    const [adoError, setAdoError] = useState<string | null>(null);

    /* ---------- ADO type filter modal ---------- */
    const ADO_WORK_ITEM_TYPE_OPTIONS = [
        { value: 'Epic',                  label: 'Epics' },
        { value: 'Feature',               label: 'Features' },
        { value: 'User Story',            label: 'User Stories' },
        { value: 'Product Backlog Item',  label: 'Product Backlog Items' },
        { value: 'Issue',                 label: 'Issues' },
        { value: 'Bug',                   label: 'Bugs' },
        { value: 'Task',                  label: 'Tasks' },
        { value: 'Test Case',             label: 'Test Cases' },
    ];
    const [showAdoTypeModal, setShowAdoTypeModal] = useState(false);
    const [adoSelectedTypes, setAdoSelectedTypes] = useState<string[]>(['Epic', 'Feature', 'User Story', 'Product Backlog Item', 'Issue', 'Bug']);

    /* ---------- Source selector state ---------- */
    // 'documents' is default; 'ado' only available for Azure projects
    const [dataSource, setDataSource] = useState<'documents' | 'ado'>('documents');
    const [showSourceModal, setShowSourceModal] = useState(false);

    /* ---------- ADO freshness confirmation state ---------- */
    const [showAdoFreshnessModal, setShowAdoFreshnessModal] = useState(false);
    // resolveAdoFreshness: true = fetch latest, false = use cached, null = cancelled (X button)
    const adoFreshnessResolveRef = useRef<((choice: boolean | null) => void) | null>(null);

    /* ---------- Review state ---------- */
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [reviewLoading, setReviewLoading] = useState(false);
    const [reviewReport, setReviewReport] = useState<ReviewRequirementsResponse | null>(null);
    const [pendingGeneratePayload, setPendingGeneratePayload] = useState<Parameters<typeof queueRequirementGeneration>[1] | null>(null);
    const [unchangedGenerateWarning, setUnchangedGenerateWarning] = useState<{
        payload: Parameters<typeof queueRequirementGeneration>[1];
        reviewPayload: Parameters<typeof RequirementService.reviewRequirements>[1];
        title: string;
        message: string;
        latestVersion?: number | null;
        analysisReport?: ReviewRequirementsResponse | null;
    } | null>(null);
    const [pendingCompletionJobId, setPendingCompletionJobId] = useState<number | null>(null);
    const [completedGenerationJob, setCompletedGenerationJob] = useState<RequirementGenerationJob | null>(null);
    const [copySuccess, setCopySuccess] = useState(false);
    /* Free-text requirement context the user optionally adds in the analysis report
       modal; passed to the test-case agent for this run only (not persisted in UI). */
    const [additionalContext, setAdditionalContext] = useState('');
    const [analysisReportJob, setAnalysisReportJob] = useState<RequirementGenerationJob | null>(null);
    const [additionalContextJob, setAdditionalContextJob] = useState<RequirementGenerationJob | null>(null);
    const [previewDataset, setPreviewDataset] = useState<any | null>(null);
    const [loadingDataset, setLoadingDataset] = useState(false);
    const reviewAnalysisFlowIdRef = useRef<string | null>(null);
    const reviewAnalysisActiveRef = useRef(false);
    const reviewAnalysisPollingRef = useRef<number | null>(null);
    const isMountedRef = useRef(false);

    /* ---------- ADO notes modal state ---------- */
    const [showAdoNotesModal, setShowAdoNotesModal] = useState(false);
    const [adoNotesDraft, setAdoNotesDraft] = useState<AdoItemNote[]>([]);
    const [adoNotesPosting, setAdoNotesPosting] = useState(false);
    const [adoNotesResult, setAdoNotesResult] = useState<{ work_item_id: number; status: string; detail?: string }[] | null>(null);

    // No auto-fetch on source switch — user must explicitly click "Sync from ADO"
    // to pull fresh data. Previously fetched items persist in local state.

    useEffect(
        function hydrateAdoLastSyncedFromProject(): void
        {
            const projectSyncedAt = currentProject?.azure_devops?.ado_work_items_last_synced_at ?? null;
            // Only set from project data when we actually have a value.
            // Do NOT reset to null — that would erase a timestamp set locally by handleSyncAdo.
            if (projectSyncedAt !== null)
            {
                setAdoLastSynced(projectSyncedAt);
            }
        },
        [currentProject?.id, currentProject?.azure_devops?.ado_work_items_last_synced_at]
    );

    useEffect(
        function markMounted(): () => void
        {
            isMountedRef.current = true;
            return () =>
            {
                isMountedRef.current = false;
                if (reviewAnalysisPollingRef.current !== null)
                {
                    window.clearInterval(reviewAnalysisPollingRef.current);
                    reviewAnalysisPollingRef.current = null;
                }
            };
        },
        []
    );

    const stopReviewAnalysisPolling = useCallback(
        function stopReviewAnalysisPollingCallback(): void
        {
            if (reviewAnalysisPollingRef.current !== null)
            {
                window.clearInterval(reviewAnalysisPollingRef.current);
                reviewAnalysisPollingRef.current = null;
            }
        },
        []
    );

    useEffect(
        function restorePersistedReviewAnalysisSession(): void
        {
            stopReviewAnalysisPolling();
            reviewAnalysisFlowIdRef.current = null;
            reviewAnalysisActiveRef.current = false;

            setShowReviewModal(false);
            setReviewLoading(false);
            setReviewReport(null);

            const persisted = readPersistedReviewAnalysisSession(projectId);
            if (!persisted)
            {
                setPendingGeneratePayload(null);
                return;
            }

            if (persisted.acknowledged)
            {
                clearPersistedReviewAnalysisSession(projectId);
                setPendingGeneratePayload(null);
                return;
            }

            reviewAnalysisFlowIdRef.current = persisted.flowId;
            reviewAnalysisActiveRef.current = true;
            setPendingGeneratePayload(persisted.pendingGeneratePayload);

            if (persisted.status === 'ready' && persisted.report)
            {
                setReviewReport(persisted.report);
                setReviewLoading(false);
                setShowReviewModal(true);
                return;
            }

            setReviewLoading(true);
            setShowReviewModal(true);

            if (typeof window !== 'undefined')
            {
                reviewAnalysisPollingRef.current = window.setInterval(() =>
                {
                    const currentFlowId = reviewAnalysisFlowIdRef.current;
                    if (!currentFlowId || !reviewAnalysisActiveRef.current)
                    {
                        stopReviewAnalysisPolling();
                        return;
                    }

                    const nextPersisted = readPersistedReviewAnalysisSession(projectId);
                    if (!nextPersisted || nextPersisted.flowId !== currentFlowId)
                    {
                        return;
                    }

                    if (nextPersisted.status !== 'ready' || !nextPersisted.report)
                    {
                        return;
                    }

                    if (!isMountedRef.current)
                    {
                        return;
                    }

                    setReviewReport(nextPersisted.report);
                    setReviewLoading(false);
                    setShowReviewModal(true);
                    stopReviewAnalysisPolling();
                }, 1200);
            }
        },
        [projectId, stopReviewAnalysisPolling]
    );

    /* ---------- Loading flags ---------- */
    const [loadingDocs, setLoadingDocs] = useState(false);
    const [loadingJobs, setLoadingJobs] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [queuingJob, setQueuingJob] = useState(false);

    /* ---------- UI state ---------- */
    const [panelMessage, setPanelMessage] = useState<PanelMessage | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [logsModal, setLogsModal] = useState<LogsModal>({ show: false, title: '' });
    const [showAllHistory, setShowAllHistory] = useState(false);

    /* ---------- Data loaders ---------- */

    const loadDocuments = useCallback(
        async function loadDocumentsCallback(): Promise<void>
        {
            setLoadingDocs(true);
            try
            {
                await fetchRequirementDocuments(projectId);
            }
            catch (err: unknown)
            {
                const e = err as { response?: { data?: { detail?: string } }; message?: string };
                setPanelMessage({ type: 'danger', text: e?.response?.data?.detail || e?.message || 'Failed to load documents' });
            }
            finally
            {
                setLoadingDocs(false);
            }
        },
        [fetchRequirementDocuments, projectId]
    );

    const loadJobs = useCallback(
        async function loadJobsCallback(): Promise<void>
        {
            setLoadingJobs(true);
            try
            {
                await fetchRequirementJobs(projectId);
            }
            catch (err: unknown)
            {
                const e = err as { response?: { data?: { detail?: string } }; message?: string };
                setPanelMessage({ type: 'danger', text: e?.response?.data?.detail || e?.message || 'Failed to load jobs' });
            }
            finally
            {
                setLoadingJobs(false);
            }
        },
        [fetchRequirementJobs, projectId]
    );

    /* ---------- Effects ---------- */

    useEffect(
        function persistRequirementUploadDraft(): void
        {
            if (uploadDraftProjectIdRef.current !== projectId)
            {
                return;
            }

            writeRequirementUploadDraft(projectId, {
                files: selectedFiles,
                fileNotes,
                fileClassifications,
            });
        },
        [fileClassifications, fileNotes, projectId, selectedFiles]
    );

    useEffect(
        function restoreRequirementUploadDraftForProject(): void
        {
            uploadDraftProjectIdRef.current = projectId;
            const draft = readRequirementUploadDraft(projectId);
            setSelectedFiles(draft.files);
            setFileNotes(draft.fileNotes);
            setFileClassifications(draft.fileClassifications);
            setUploadError(null);
        },
        [projectId]
    );

    useEffect(
        function initialLoad()
        {
            loadDocuments();
            loadJobs();
        },
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
        [projectId]
    );

    // Auto-select all ready documents whenever the ready list changes
    useEffect(
        function syncDocSelection()
        {
            setSelectedDocIds(readyDocuments.map((d) => d.id));
        },
        [readyDocuments]
    );

    useEffect(
        function pollRequirementJobs()
        {
            const interval = setInterval(() =>
            {
                fetchRequirementJobs(projectId).catch(() => undefined);
            }, JOB_POLL_INTERVAL_MS);

            return () => clearInterval(interval);
        },
        [fetchRequirementJobs, projectId]
    );

    useEffect(
        function pollProcessingDocuments()
        {
            if (!hasProcessingDocuments) return undefined;

            const interval = setInterval(() =>
            {
                fetchRequirementDocuments(projectId).catch(() => undefined);
            }, DOCUMENT_POLL_INTERVAL_MS);

            return () => clearInterval(interval);
        },
        [hasProcessingDocuments, fetchRequirementDocuments, projectId]
    );

    useEffect(
        function watchQueuedGenerationCompletion(): void
        {
            if (pendingCompletionJobId == null) return;

            const trackedJob = jobs.find((job) => job.id === pendingCompletionJobId);
            if (!trackedJob) return;

            const status = trackedJob.status.toLowerCase();
            if (status === 'completed')
            {
                setPendingCompletionJobId(null);
                setCompletedGenerationJob(trackedJob);
                return;
            }

            if (status === 'failed' || status === 'cancelled')
            {
                setPendingCompletionJobId(null);
                setPanelMessage({
                    type: 'danger',
                    text: trackedJob.error_message || `Generation ${trackedJob.version_number != null ? `v${trackedJob.version_number}` : `#${trackedJob.id}`} ${status}.`,
                });
            }
        },
        [jobs, pendingCompletionJobId]
    );

    /* ---------- Upload handlers ---------- */

    function handleDropZoneClick(): void
    {
        fileInputRef.current?.click();
    }

    function handleFileInputChange(event: React.ChangeEvent<HTMLInputElement>): void
    {
        const picked = Array.from(event.target.files ?? []);
        mergeFiles(picked);
        if (fileInputRef.current) fileInputRef.current.value = '';
    }

    function mergeFiles(incoming: File[]): void
    {
        const unsupportedFiles = incoming.filter((file) => !ACCEPTED_EXTENSIONS.has(`.${file.name.split('.').pop()?.toLowerCase() || ''}`));
        if (unsupportedFiles.length > 0)
        {
            setUploadError(`Unsupported file type: ${unsupportedFiles.map((file) => file.name).join(', ')}`);
            return;
        }

        const emptyFiles = incoming.filter((file) => file.size === 0);
        if (emptyFiles.length > 0)
        {
            setUploadError(
                emptyFiles.length === 1
                    ? `"${emptyFiles[0].name}" is empty and cannot be uploaded.`
                    : `${emptyFiles.length} empty files cannot be uploaded.`,
            );
            return;
        }

        const oversizedFiles = incoming.filter((file) => file.size > MAX_UPLOAD_FILE_BYTES);
        if (oversizedFiles.length > 0)
        {
            setUploadError(`Each file must be 25 MB or smaller. Remove: ${oversizedFiles.map((file) => file.name).join(', ')}`);
            return;
        }

        if (incoming.length === 0)
        {
            return;
        }

        setSelectedFiles((prev) =>
        {
            const existingNames = new Set(prev.map((f) => f.name));
            const unique = incoming.filter((f) => !existingNames.has(f.name));
            const merged = [...prev, ...unique];
            if (merged.length > MAX_FILES_PER_UPLOAD)
            {
                setUploadError(`You can upload a maximum of ${MAX_FILES_PER_UPLOAD} files at once. Remove some files and try again.`);
                return prev;
            }
            setUploadError(null);
            return merged;
        });
    }

    function handleRemoveFile(index: number): void
    {
        setSelectedFiles((prev) =>
        {
            const removed = prev[index];
            if (removed)
            {
                setFileNotes((prevNotes) => { const copy = { ...prevNotes }; delete copy[removed.name]; return copy; });
                setFileClassifications((prev2) => { const copy = { ...prev2 }; delete copy[removed.name]; return copy; });
            }
            return prev.filter((_, i) => i !== index);
        });
    }

    function handleDragOver(event: React.DragEvent<HTMLDivElement>): void
    {
        event.preventDefault();
        setIsDragOver(true);
    }

    function handleDragLeave(): void
    {
        setIsDragOver(false);
    }

    function handleDrop(event: React.DragEvent<HTMLDivElement>): void
    {
        event.preventDefault();
        setIsDragOver(false);
        const dropped = Array.from(event.dataTransfer.files);
        mergeFiles(dropped);
    }

    async function handleUploadSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void>
    {
        event.preventDefault();

        if (selectedFiles.length === 0)
        {
            setUploadError('Select at least one file to upload.');
            return;
        }

        // Validate per-file classification
        const missingClass = selectedFiles.find((f) => !fileClassifications[f.name]);
        if (missingClass)
        {
            setUploadError(`Please select a classification for "${missingClass.name}".`);
            return;
        }

        // Validate per-file context descriptions
        const missingNotes = selectedFiles.find((f) => !(fileNotes[f.name] || '').trim());
        if (missingNotes)
        {
            setUploadError(`Please add a context description for "${missingNotes.name}".`);
            return;
        }

        setUploading(true);
        setUploadError(null);

        try
        {
            // Upload each file individually so it gets its own context description
            for (const file of selectedFiles)
            {
                if (file.size === 0)
                {
                    setUploadError(EMPTY_FILE_ERROR);
                    setUploading(false);
                    return;
                }
                await RequirementService.uploadSingleRequirementDocument(projectId, file, {
                    classification: fileClassifications[file.name] as DocumentClassification,
                    notes: (fileNotes[file.name] || '').trim() || undefined,
                });
            }
            await fetchRequirementDocuments(projectId);
            setPanelMessage({
                type: 'success',
                text: `${selectedFiles.length} document${selectedFiles.length === 1 ? '' : 's'} uploaded.`
            });
            clearRequirementUploadDraft(projectId);
            setSelectedFiles([]);
            setFileNotes({});
            setFileClassifications({});
        }
        catch (err: unknown)
        {
            setUploadError(formatApiError(err, 'Upload failed'));
        }
        finally
        {
            setUploading(false);
        }
    }

    /* ---------- Document action handlers ---------- */

    async function handleDownloadDocument(document: RequirementDocument): Promise<void>
    {
        try
        {
            const blob = await downloadRequirementDocument(projectId, document.id);
            triggerDownloadFromBlob(blob, document.original_filename);
        }
        catch (err: unknown)
        {
            const e = err as { response?: { data?: { detail?: string } }; message?: string };
            setPanelMessage({ type: 'danger', text: e?.response?.data?.detail || e?.message || 'Failed to download' });
        }
    }

    async function handleReprocessDocument(document: RequirementDocument): Promise<void>
    {
        try
        {
            await reprocessRequirementDocument(projectId, document.id);
            setPanelMessage({ type: 'info', text: `Reprocessing started for ${document.original_filename}.` });
        }
        catch (err: unknown)
        {
            const e = err as { response?: { data?: { detail?: string } }; message?: string };
            setPanelMessage({ type: 'danger', text: e?.response?.data?.detail || e?.message || 'Failed to reprocess' });
        }
    }

    async function handleDeleteDocument(document: RequirementDocument): Promise<void>
    {
        const confirmed = window.confirm(`Delete "${document.original_filename}"? This cannot be undone.`);
        if (!confirmed) return;

        try
        {
            await deleteRequirementDocument(projectId, document.id);
            setPanelMessage({ type: 'success', text: `${document.original_filename} deleted.` });
            await fetchRequirementDocuments(projectId);
        }
        catch (err: unknown)
        {
            const e = err as { response?: { data?: { detail?: string } }; message?: string };
            setPanelMessage({ type: 'danger', text: e?.response?.data?.detail || e?.message || 'Failed to delete' });
        }
    }

    /* ---------- ADO handlers ---------- */

    async function handleSyncAdo(types: string[]): Promise<AdoWorkItem[]>
    {
        setShowAdoTypeModal(false);
        setAdoSyncing(true);
        setAdoError(null);

        try
        {
            const params = types.length > 0 ? `?work_item_types=${encodeURIComponent(types.join(','))}` : '';
            const response = await apiClient.get<AdoWorkItemsResponse>(
                API_ENDPOINTS.PROJECT_REQUIREMENT_ADO_WORK_ITEMS(projectId) + params
            );
            const data = response.data;
            const items = data.items || [];
            setAdoWorkItems(items);
            const syncedAt = data.last_synced_at ?? new Date().toISOString();
            setAdoLastSynced(syncedAt);
            setSelectedAdoIds(items.map((i) => i.id));
            // Persist items so they survive navigation
            try { localStorage.setItem(adoStorageKey, JSON.stringify(items)); } catch { /* non-critical */ }
            // Update the project store so the timestamp persists across navigation
            refreshProject(projectId).catch(() => { /* non-critical */ });
            return items;
        }
        catch (err: unknown)
        {
            const e = err as { response?: { data?: { detail?: string } }; message?: string };
            setAdoError(e?.response?.data?.detail || e?.message || 'Failed to sync ADO work items');
            return [];
        }
        finally
        {
            setAdoSyncing(false);
        }
    }

    function handleToggleAdoItem(id: number): void
    {
        setSelectedAdoIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    }

    function handleSelectAllAdo(): void
    {
        setSelectedAdoIds(adoWorkItems.map((i) => i.id));
    }

    function handleDeselectAllAdo(): void
    {
        setSelectedAdoIds([]);
    }

    /* ---------- Generation handlers ---------- */

    async function runReviewAnalysis(
        payload: Parameters<typeof queueRequirementGeneration>[1],
        reviewPayload: Parameters<typeof RequirementService.reviewRequirements>[1],
    ): Promise<void>
    {
        const flowId = makeReviewAnalysisFlowId();
        reviewAnalysisFlowIdRef.current = flowId;
        reviewAnalysisActiveRef.current = true;
        stopReviewAnalysisPolling();
        setPendingGeneratePayload(payload);
        setReviewLoading(true);
        setReviewReport(null);
        setShowReviewModal(true);
        writePersistedReviewAnalysisSession(projectId, {
            flowId,
            status: 'loading',
            readyShown: false,
            acknowledged: false,
            pendingGeneratePayload: payload,
            report: null,
            updatedAt: new Date().toISOString(),
        });
        window.dispatchEvent(new Event(REQUIREMENTS_GENERATE_REVIEW_EVENT));

        try
        {
            const report = await RequirementService.reviewRequirements(projectId, reviewPayload);
            if (reviewAnalysisFlowIdRef.current !== flowId || !reviewAnalysisActiveRef.current)
            {
                return;
            }

            writePersistedReviewAnalysisSession(projectId, {
                flowId,
                status: 'ready',
                readyShown: true,
                acknowledged: false,
                pendingGeneratePayload: payload,
                report,
                updatedAt: new Date().toISOString(),
            });

            if (isMountedRef.current)
            {
                setReviewReport(report);
                setReviewLoading(false);
                setShowReviewModal(true);
            }
        }
        catch
        {
            const fallbackReport: ReviewRequirementsResponse = {
                summary: 'Review service is temporarily unavailable.',
                overall_quality: 'fair',
                issues: [],
                ado_item_notes: [],
                doc_item_notes: [],
            };

            if (reviewAnalysisFlowIdRef.current !== flowId || !reviewAnalysisActiveRef.current)
            {
                return;
            }

            writePersistedReviewAnalysisSession(projectId, {
                flowId,
                status: 'ready',
                readyShown: true,
                acknowledged: false,
                pendingGeneratePayload: payload,
                report: fallbackReport,
                updatedAt: new Date().toISOString(),
            });

            if (isMountedRef.current)
            {
                setReviewReport(fallbackReport);
                setReviewLoading(false);
                setShowReviewModal(true);
            }
        }
        finally
        {
            if (isMountedRef.current && reviewAnalysisFlowIdRef.current === flowId && reviewAnalysisActiveRef.current)
            {
                setReviewLoading(false);
            }
        }
    }

    async function handleGenerate(): Promise<void>
    {
        // Build single-source payload based on dataSource
        const payload: Parameters<typeof queueRequirementGeneration>[1] = {
            include_artifacts: { requirements: true, test_cases: true },
        };
        let reviewAdoIds = selectedAdoIds;
        let reviewAdoItems = adoWorkItems;

        if (dataSource === 'ado')
        {
            if (selectedAdoIds.length === 0)
            {
                setPanelMessage({ type: 'danger', text: 'Select at least one ADO work item to generate from.' });
                return;
            }
            // Ask user if they want to re-sync ADO first
            const choice = await new Promise<boolean | null>((resolve) =>
            {
                adoFreshnessResolveRef.current = resolve;
                setShowAdoFreshnessModal(true);
            });
            if (choice === null) return;
            let effectiveAdoItems = adoWorkItems;
            if (choice)
            {
                const syncedItems = await handleSyncAdo(adoSelectedTypes);
                if (syncedItems.length === 0)
                {
                    return;
                }
                effectiveAdoItems = syncedItems;
            }

            const effectiveSelectedIds = choice
                ? effectiveAdoItems.map((item) => item.id)
                : selectedAdoIds;
            const effectiveSelectedItems = effectiveAdoItems.filter((item) => effectiveSelectedIds.includes(item.id));

            if (effectiveSelectedItems.length === 0)
            {
                setPanelMessage({ type: 'danger', text: 'Select at least one ADO work item to generate from.' });
                return;
            }

            reviewAdoIds = effectiveSelectedItems.map((item) => item.id);
            reviewAdoItems = effectiveSelectedItems;
            payload.ado_work_item_ids = effectiveSelectedItems.map((item) => item.id);
            payload.ado_work_items = effectiveSelectedItems;
        }
        else
        {
            if (readyDocuments.length === 0)
            {
                setPanelMessage({ type: 'danger', text: 'Upload at least one ready document to generate from.' });
                return;
            }
            if (selectedDocIds.length === 0)
            {
                setPanelMessage({ type: 'danger', text: 'Select at least one document to generate from.' });
                return;
            }
            payload.document_ids = selectedDocIds;
        }

        // Build review payload
        const reviewPayload: Parameters<typeof RequirementService.reviewRequirements>[1] = {};
        if (dataSource === 'ado')
        {
            reviewPayload.ado_work_item_ids = reviewAdoIds;
            reviewPayload.ado_work_items = reviewAdoItems
                .map((i) => ({ id: i.id, title: i.title, type: i.type, state: i.state }));
        }
        else
        {
            reviewPayload.document_ids = selectedDocIds;
        }

        try
        {
            const preflightPayload = dataSource === 'ado'
                ? {
                    ado_work_item_ids: reviewAdoIds,
                    ado_work_items: reviewAdoItems,
                    include_artifacts: payload.include_artifacts,
                }
                : {
                    document_ids: selectedDocIds,
                    include_artifacts: payload.include_artifacts,
                };
            const preflight = await RequirementService.preflightRequirementGeneration(projectId, preflightPayload);
            if (preflight.unchanged)
            {
                const isAdo = dataSource === 'ado';
                setUnchangedGenerateWarning({
                    payload: { ...payload, force: true },
                    reviewPayload,
                    title: isAdo ? 'Azure DevOps Work Items Unchanged' : 'Requirement Documents Unchanged',
                    latestVersion: preflight.latest_version_number,
                    message: preflight.message || (isAdo
                        ? 'Nothing has changed in the selected Azure DevOps work items since the last generation. Regenerating will re-run analysis and use tokens.'
                        : 'Nothing has changed in the selected requirement documents since the last generation. Regenerating will re-run analysis and use tokens.'),
                    analysisReport: preflight.analysis_report ?? null,
                });
                return;
            }
        }
        catch (err: unknown)
        {
            const e = err as { response?: { data?: { detail?: string | { message?: string } } }; message?: string };
            const detail = e?.response?.data?.detail;
            const detailMessage = typeof detail === 'string' ? detail : detail?.message;
            setPanelMessage({ type: 'danger', text: detailMessage || e?.message || 'Failed to check selected requirements before generation.' });
            return;
        }

        await runReviewAnalysis(payload, reviewPayload);
    }

    async function handleConfirmGenerate(): Promise<void>
    {
        if (!pendingGeneratePayload) return;
        reviewAnalysisActiveRef.current = false;
        reviewAnalysisFlowIdRef.current = null;
        stopReviewAnalysisPolling();
        clearPersistedReviewAnalysisSession(projectId);
        setShowReviewModal(false);
        const reportToSave = reviewReport;
        setReviewReport(null);
        const trimmedContext = additionalContext.trim();
        const payload = {
            ...pendingGeneratePayload,
            ...(reportToSave ? { analysis_report: reportToSave } : {}),
            ...(trimmedContext ? { additional_context: trimmedContext } : {}),
        };
        setPendingGeneratePayload(null);
        setAdditionalContext('');

        setQueuingJob(true);
        try
        {
            const job = await queueRequirementGeneration(projectId, payload);
            await fetchRequirementJobs(projectId);
            setPendingCompletionJobId(job.id);
            setPanelMessage({
                type: 'success',
                text: `Generation ${job.version_number != null ? `v${job.version_number}` : `#${job.id}`} started. You can stay here while it runs.`,
            });
        }
        catch (err: unknown)
        {
            const e = err as { response?: { data?: { detail?: string | { message?: string } } }; message?: string };
            const detail = e?.response?.data?.detail;
            const detailMessage = typeof detail === 'string' ? detail : detail?.message;
            setPanelMessage({ type: 'danger', text: detailMessage || e?.message || 'Failed to queue job' });
        }
        finally
        {
            setQueuingJob(false);
        }
    }

    async function handleDownloadSyntheticCSV(job?: RequirementGenerationJob, datasetId?: string): Promise<void>
    {
        try
        {
            let id = datasetId;
            if (!id && job)
            {
                const manifest = JSON.parse((job as any).output_manifest || '{}');
                id = manifest.dataset_id;
            }

            if (!id)
            {
                alert('No dataset found.');
                return;
            }
            const response = await apiClient.get(API_ENDPOINTS.TEST_DATA_DOWNLOAD(projectId, id), { responseType: 'blob' });
            triggerDownloadFromBlob(
                response.data,
                filenameFromContentDisposition(
                    response.headers?.['content-disposition'],
                    `synthetic_data_${id}.csv`,
                ),
            );
        }
        catch (err)
        {
            console.error('Failed to download synthetic data:', err);
            setPanelMessage({ type: 'danger', text: 'Failed to download CSV.' });
        }
    }

    async function handleViewSyntheticData(job: RequirementGenerationJob): Promise<void>
    {
        try
        {
            setLoadingDataset(true);
            const manifest = JSON.parse((job as any).output_manifest || '{}');
            const datasetId = manifest.dataset_id;
            if (!datasetId)
            {
                alert('No dataset found for this job.');
                return;
            }
            const response = await apiClient.get(API_ENDPOINTS.TEST_DATA_DETAIL(projectId, datasetId));
            setPreviewDataset(response.data);
        }
        catch (err)
        {
            console.error('Failed to load synthetic data:', err);
            setPanelMessage({ type: 'danger', text: 'Failed to load data preview.' });
        }
        finally
        {
            setLoadingDataset(false);
        }
    }

    /* ---------- Job action handlers ---------- */

    function handleViewResults(job: RequirementGenerationJob): void
    {
        if (!jobHasTestCases(job))
        {
            setPanelMessage({ type: 'info', text: 'This job does not include generated test cases yet.' });
            return;
        }

        if (onSelectJobId)
        {
            onSelectJobId(job.id);
            return;
        }

        navigate(projectTestCasesRoute(projectId, { jobId: job.id }));
    }

    async function handleViewLogs(job: RequirementGenerationJob): Promise<void>
    {
        const title = `Job #${job.id} — workflow.log`;
        setLogsModal({ show: true, title, jobId: job.id, status: job.status });
    }

    function handleCloseLogsModal(): void
    {
        setLogsModal({ show: false, title: '' });
    }

    /* ============================================================
       Render helpers
       ============================================================ */

    function renderClassificationBadge(cls?: DocumentClassification | null): React.ReactNode
    {
        if (!cls) return null;
        if (cls === 'new_feature')
        {
            return <span className="rp-classification-badge rp-classification-badge--new">New Functionality</span>;
        }
        if (cls === 'something_else')
        {
            return <span className="rp-classification-badge rp-classification-badge--other">Something Else</span>;
        }
        return <span className="rp-classification-badge rp-classification-badge--update">Existing Functionality Change</span>;
    }

    /* ---- Source selector header (Azure projects only) ---- */

    function renderSourceToggleBar(): React.ReactNode
    {
        if (!isAzureProject) return null;
        return (
            <div className="rp-source-bar">
                <span className="rp-source-bar-label">Source:</span>
                <span className={`rp-source-chip ${dataSource === 'documents' ? 'rp-source-chip--active' : ''}`}>
                    {dataSource === 'documents' ? 'Upload Documents' : 'Azure DevOps'}
                </span>
                <button
                    className="rp-btn-secondary"
                    type="button"
                    style={{ fontSize: '0.78rem', padding: '4px 10px' }}
                    onClick={() => setShowSourceModal(true)}
                >
                    Change Source
                </button>
            </div>
        );
    }

    function renderAdditionalContextModal(): React.ReactNode
    {
        const context = (additionalContextJob?.additional_context || '').trim();
        const versionNumber = additionalContextJob?.version_number;

        return (
            <Modal show={!!additionalContextJob} onHide={() => setAdditionalContextJob(null)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '0.95rem', fontWeight: 700 }}>
                        Added Requirement Context{versionNumber != null ? ` - v${versionNumber}` : ''}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                    <p style={{ fontSize: '0.83rem', color: '#6b7280', marginBottom: 12, lineHeight: 1.5 }}>
                        Extra requirement context provided for this generation. It was used together with the
                        source document(s) to steer test-case generation.
                    </p>
                    {context
                        ? (
                            <div
                                style={{
                                    padding: '12px 14px',
                                    background: '#f9fafb',
                                    borderRadius: 8,
                                    border: '1px solid #e8ecf0',
                                    fontSize: '0.87rem',
                                    color: '#374151',
                                    lineHeight: 1.6,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {context}
                            </div>
                        )
                        : (
                            <Alert variant="info" style={{ margin: 0 }}>
                                No additional context was provided for this generation.
                            </Alert>
                        )
                    }
                </Modal.Body>

                <Modal.Footer>
                    <button
                        className="rp-btn-secondary"
                        type="button"
                        onClick={() => setAdditionalContextJob(null)}
                    >
                        Close
                    </button>
                </Modal.Footer>
            </Modal>
        );
    }

    function renderSavedAnalysisReportModal(): React.ReactNode
    {
        const report = analysisReportJob?.analysis_report;
        const versionNumber = analysisReportJob?.version_number;

        return (
            <Modal show={!!analysisReportJob} onHide={() => setAnalysisReportJob(null)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '0.95rem', fontWeight: 700 }}>
                        Requirements Analysis Report{versionNumber != null ? ` - v${versionNumber}` : ''}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                    {!report
                        ? (
                            <Alert variant="info" style={{ margin: 0 }}>
                                No saved analysis report is available for this generation.
                            </Alert>
                        )
                        : (
                            <>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                                    <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>Overall Quality:</span>
                                    <span style={{
                                        fontWeight: 700,
                                        color: qualityColor(report.overall_quality),
                                        textTransform: 'capitalize',
                                        fontSize: '0.85rem'
                                    }}>
                                        {report.overall_quality}
                                    </span>
                                </div>

                                <p style={{ fontSize: '0.87rem', color: '#374151', marginBottom: 16, lineHeight: 1.6 }}>
                                    {report.summary}
                                </p>

                                {report.issues && report.issues.length > 0 && (
                                    <div>
                                        <p style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 8 }}>
                                            Flagged Items ({report.issues.length})
                                        </p>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {report.issues.map((issue, idx) => (
                                                <div
                                                    key={idx}
                                                    style={{
                                                        display: 'flex',
                                                        gap: 10,
                                                        alignItems: 'flex-start',
                                                        padding: '8px 12px',
                                                        background: '#f9fafb',
                                                        borderRadius: 6,
                                                        borderLeft: `3px solid ${issue.severity === 'high' ? '#dc3545' : issue.severity === 'medium' ? '#fd7e14' : '#6c757d'}`
                                                    }}
                                                >
                                                    {severityIcon(issue.severity)}
                                                    <div style={{ flex: 1 }}>
                                                        <span style={{
                                                            fontSize: '0.72rem',
                                                            fontWeight: 700,
                                                            textTransform: 'uppercase',
                                                            color: '#9ca3af',
                                                            marginRight: 8
                                                        }}>
                                                            {issue.type}
                                                        </span>
                                                        <span style={{ fontSize: '0.84rem', color: '#374151' }}>
                                                            {issue.description}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {report.issues && report.issues.length === 0 && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#198754', fontSize: '0.87rem' }}>
                                        <CheckCircleFill size={15} />
                                        No issues found. Your requirements look good!
                                    </div>
                                )}

                                {report.doc_item_notes && report.doc_item_notes.length > 0 && (
                                    <div style={{ marginTop: 20 }}>
                                        <p style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 12 }}>
                                            Per Document Analysis
                                        </p>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            {report.doc_item_notes.map((doc, idx) => (
                                                <div
                                                    key={idx}
                                                    style={{
                                                        padding: '10px 14px',
                                                        background: '#f9fafb',
                                                        borderRadius: 8,
                                                        border: '1px solid #e8ecf0',
                                                    }}
                                                >
                                                    <div style={{ fontWeight: 700, fontSize: '0.83rem', color: '#374151', marginBottom: doc.note ? 6 : 0 }}>
                                                        {doc.doc_title}
                                                    </div>
                                                    {doc.note
                                                        ? <p style={{ fontSize: '0.83rem', color: '#5f6f82', margin: 0, lineHeight: 1.6 }}>{doc.note}</p>
                                                        : <p style={{ fontSize: '0.83rem', color: '#9ca3af', margin: 0, fontStyle: 'italic' }}>No issues found for this document.</p>
                                                    }
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {report.ado_item_notes && report.ado_item_notes.length > 0 && (
                                    <div style={{ marginTop: 20 }}>
                                        <p style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 12 }}>
                                            Per Work Item Analysis
                                        </p>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            {report.ado_item_notes.map((item) => (
                                                <div
                                                    key={item.work_item_id}
                                                    style={{
                                                        padding: '10px 14px',
                                                        background: '#f9fafb',
                                                        borderRadius: 8,
                                                        border: '1px solid #e8ecf0',
                                                    }}
                                                >
                                                    <div style={{ fontWeight: 700, fontSize: '0.83rem', color: '#374151', marginBottom: item.note ? 6 : 0 }}>
                                                        #{item.work_item_id} - {item.work_item_title}
                                                    </div>
                                                    {item.note
                                                        ? <p style={{ fontSize: '0.83rem', color: '#5f6f82', margin: 0, lineHeight: 1.6 }}>{item.note}</p>
                                                        : <p style={{ fontSize: '0.83rem', color: '#9ca3af', margin: 0, fontStyle: 'italic' }}>No issues found for this item.</p>
                                                    }
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )
                    }
                </Modal.Body>

                <Modal.Footer>
                    <button
                        className="rp-btn-secondary"
                        type="button"
                        onClick={() => setAnalysisReportJob(null)}
                    >
                        Close
                    </button>
                </Modal.Footer>
            </Modal>
        );
    }

    /* ---- Source selector modal ---- */

    function renderSourceModal(): React.ReactNode
    {
        return (
            <Modal show={showSourceModal} onHide={() => setShowSourceModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '0.95rem', fontWeight: 700 }}>
                        Choose Generation Source
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ fontSize: '0.87rem', color: '#5f6f82', marginBottom: 16 }}>
                        Select the source you want to use for this test generation job.
                        Each job uses a single source.
                    </p>
                    <div style={{ display: 'flex', gap: 12 }}>
                        <div
                            className={`rp-source-card ${dataSource === 'documents' ? 'rp-source-card--selected' : ''}`}
                            onClick={() => { setDataSource('documents'); setShowSourceModal(false); }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter') { setDataSource('documents'); setShowSourceModal(false); } }}
                        >
                            <CloudUploadFill size={24} style={{ marginBottom: 8 }} />
                            <p style={{ fontWeight: 700, margin: '0 0 4px' }}>Upload Documents</p>
                            <p style={{ fontSize: '0.78rem', color: '#9ca3af', margin: 0 }}>PDF, DOCX, TXT, Markdown</p>
                            {dataSource === 'documents' && (
                                <span style={{ position: 'absolute', top: 10, right: 10, color: '#0d6efd' }}>
                                    <CheckCircleFill size={16} />
                                </span>
                            )}
                        </div>

                        <div
                            className={`rp-source-card ${dataSource === 'ado' ? 'rp-source-card--selected' : ''}`}
                            onClick={() => { setDataSource('ado'); setShowSourceModal(false); }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter') { setDataSource('ado'); setShowSourceModal(false); } }}
                        >
                            <ArrowRepeat size={24} style={{ marginBottom: 8 }} />
                            <p style={{ fontWeight: 700, margin: '0 0 4px' }}>Import from ADO</p>
                            <p style={{ fontSize: '0.78rem', color: '#9ca3af', margin: 0 }}>Azure DevOps work items</p>
                            {dataSource === 'ado' && (
                                <span style={{ position: 'absolute', top: 10, right: 10, color: '#0d6efd' }}>
                                    <CheckCircleFill size={16} />
                                </span>
                            )}
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        );
    }

    /* ---- ADO freshness confirmation modal ---- */

    function renderAdoFreshnessModal(): React.ReactNode
    {
        const displaySyncTime = adoLastSynced
            || currentProject?.azure_devops?.ado_work_items_last_synced_at
            || null;
        return (
            <>
            {/* ---- ADO work item type selection modal ---- */}
            <Modal show={showAdoTypeModal} onHide={() => setShowAdoTypeModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '0.95rem', fontWeight: 700 }}>
                        Select Work Item Types to Fetch
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ fontSize: '0.85rem', color: '#5f6f82', marginBottom: '14px' }}>
                        Choose which Azure DevOps work item types to include in the sync.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {ADO_WORK_ITEM_TYPE_OPTIONS.map(({ value, label }) => (
                            <label key={value} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '0.87rem', color: '#374151' }}>
                                <input
                                    type="checkbox"
                                    checked={adoSelectedTypes.includes(value)}
                                    onChange={() => {
                                        setAdoSelectedTypes((prev) =>
                                            prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value]
                                        );
                                    }}
                                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                />
                                {label}
                            </label>
                        ))}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button className="rp-btn-secondary" type="button" onClick={() => setShowAdoTypeModal(false)}>
                        Cancel
                    </button>
                    <button
                        className="rp-btn-primary"
                        type="button"
                        disabled={adoSelectedTypes.length === 0}
                        onClick={() => handleSyncAdo(adoSelectedTypes)}
                    >
                        <ArrowRepeat size={13} />
                        Sync
                    </button>
                </Modal.Footer>
            </Modal>

            <Modal show={showAdoFreshnessModal} onHide={() => { setShowAdoFreshnessModal(false); adoFreshnessResolveRef.current?.(null); }} centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '0.95rem', fontWeight: 700 }}>
                        Azure DevOps Work Items
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ fontSize: '0.87rem', color: '#5f6f82' }}>
                        Do you want to fetch the latest work items from Azure DevOps before generating,
                        or use the currently synced data?
                    </p>
                    {displaySyncTime && (
                        <p style={{ fontSize: '0.78rem', color: '#9ca3af' }}>
                            Last synced: {formatLocalDateTimeFromUTC(displaySyncTime)}
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button
                        className="rp-btn-secondary"
                        type="button"
                        onClick={() => { setShowAdoFreshnessModal(false); adoFreshnessResolveRef.current?.(true); }}
                    >
                        <ArrowRepeat size={13} />
                        Fetch Latest
                    </button>
                    <button
                        className="rp-btn-primary"
                        type="button"
                        onClick={() => { setShowAdoFreshnessModal(false); adoFreshnessResolveRef.current?.(false); }}
                    >
                        Use Last Synced
                    </button>
                </Modal.Footer>
            </Modal>
            </>
        );
    }

    function renderUnchangedGenerateWarningModal(): React.ReactNode
    {
        const warning = unchangedGenerateWarning;
        return (
            <Modal show={warning !== null} onHide={() => setUnchangedGenerateWarning(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '0.95rem', fontWeight: 700 }}>
                        {warning?.title || 'Requirements Unchanged'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                        <ExclamationTriangleFill size={20} style={{ color: '#d97706', flexShrink: 0, marginTop: 2 }} />
                        <div>
                            <p style={{ fontSize: '0.9rem', color: '#374151', marginBottom: 8 }}>
                                {warning?.message || 'Nothing has changed in the selected requirement documents since the last generation.'}
                            </p>
                            {warning?.analysisReport && (
                                <p style={{ fontSize: '0.82rem', color: '#64748b', margin: '0 0 8px' }}>
                                    The latest saved analysis report will be reused, so requirements analysis will not run again.
                                </p>
                            )}
                            {warning?.latestVersion != null && (
                                <p style={{ fontSize: '0.82rem', color: '#64748b', margin: 0 }}>
                                    Latest matching generation: v{warning.latestVersion}
                                </p>
                            )}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        className="rp-btn-secondary"
                        type="button"
                        onClick={() => setUnchangedGenerateWarning(null)}
                    >
                        Cancel
                    </button>
                    {warning?.analysisReport && (
                        <button
                            className="rp-btn-secondary"
                            type="button"
                            onClick={() => {
                                if (!warning) return;
                                setUnchangedGenerateWarning(null);
                                void runReviewAnalysis(warning.payload, warning.reviewPayload);
                            }}
                        >
                            Regenerate Report
                        </button>
                    )}
                    <button
                        className="rp-generate-btn"
                        type="button"
                        onClick={() => {
                            if (!warning) return;
                            setUnchangedGenerateWarning(null);
                            if (warning.analysisReport)
                            {
                                reviewAnalysisActiveRef.current = false;
                                reviewAnalysisFlowIdRef.current = null;
                                stopReviewAnalysisPolling();
                                setPendingGeneratePayload(warning.payload);
                                setReviewLoading(false);
                                setReviewReport(warning.analysisReport);
                                setShowReviewModal(true);
                                clearPersistedReviewAnalysisSession(projectId);
                                return;
                            }
                            void runReviewAnalysis(warning.payload, warning.reviewPayload);
                        }}
                    >
                        <Lightning size={14} style={{ marginRight: 5 }} />
                        {warning?.analysisReport ? 'Use Saved Report' : 'Continue Anyway'}
                    </button>
                </Modal.Footer>
            </Modal>
        );
    }

    /* ---- Review modal ---- */

    function handleOpenAdoNotes(): void
    {
        if (!reviewReport?.ado_item_notes) return;
        // Pre-populate draft with all work items (including those with empty notes)
        setAdoNotesDraft(reviewReport.ado_item_notes.map((n) => ({ ...n })));
        setAdoNotesResult(null);
        setShowAdoNotesModal(true);
    }

    async function handlePostAdoNotes(): Promise<void>
    {
        setAdoNotesPosting(true);
        try
        {
            const res = await RequirementService.postAdoNotes(projectId, adoNotesDraft);
            setAdoNotesResult(res.results);
        }
        catch
        {
            setAdoNotesResult([{ work_item_id: 0, status: 'error', detail: 'Request failed. Please try again.' }]);
        }
        finally
        {
            setAdoNotesPosting(false);
        }
    }

    function severityIcon(severity: ReviewIssue['severity']): React.ReactNode
    {
        if (severity === 'high') return <ExclamationCircleFill size={13} color="#dc3545" style={{ flexShrink: 0 }} />;
        if (severity === 'medium') return <ExclamationTriangleFill size={13} color="#fd7e14" style={{ flexShrink: 0 }} />;
        return <ExclamationTriangleFill size={13} color="#6c757d" style={{ flexShrink: 0 }} />;
    }

    function qualityColor(q?: string): string
    {
        if (q === 'good') return '#198754';
        if (q === 'poor') return '#dc3545';
        return '#fd7e14';
    }

    function renderReviewModal(): React.ReactNode
    {
        const handleCloseReview = () =>
        {
            setShowReviewModal(false);
            if (reviewReport)
            {
                reviewAnalysisActiveRef.current = false;
                reviewAnalysisFlowIdRef.current = null;
                stopReviewAnalysisPolling();
                clearPersistedReviewAnalysisSession(projectId);
                setReviewReport(null);
                setReviewLoading(false);
                setPendingGeneratePayload(null);
                setAdditionalContext('');
                return;
            }
        };

        const handleCopyAnalysis = () => {
            if (!reviewReport) return;
            const lines: string[] = [
                `Overall Quality: ${reviewReport.overall_quality}`,
                '',
                reviewReport.summary,
            ];
            if (reviewReport.issues && reviewReport.issues.length > 0) {
                lines.push('', `Flagged Items (${reviewReport.issues.length}):`);
                reviewReport.issues.forEach((issue) => {
                    lines.push(`- [${issue.severity.toUpperCase()}] ${issue.type}: ${issue.description}`);
                });
            }
            if (reviewReport.doc_item_notes && reviewReport.doc_item_notes.length > 0) {
                lines.push('', 'Per Document Analysis:');
                reviewReport.doc_item_notes.forEach((doc) => {
                    lines.push(`\n${doc.doc_title}:`);
                    lines.push(doc.note || 'No issues found for this document.');
                });
            }
            if (reviewReport.ado_item_notes && reviewReport.ado_item_notes.length > 0) {
                lines.push('', 'Per Work Item Analysis:');
                reviewReport.ado_item_notes.forEach((item) => {
                    lines.push(`\n#${item.work_item_id} — ${item.work_item_title}:`);
                    lines.push(item.note || 'No issues found for this item.');
                });
            }
            navigator.clipboard.writeText(lines.join('\n')).then(() => {
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 2500);
            }).catch(() => undefined);
        };

        return (
            <>
            <Modal show={showReviewModal} onHide={handleCloseReview} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '0.95rem', fontWeight: 700 }}>
                        Requirements Analysis Report
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                    {reviewLoading
                        ? (
                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                <Spinner animation="border" size="sm" />
                                <p style={{ marginTop: 12, fontSize: '0.85rem', color: '#9ca3af' }}>Analysing your requirements…</p>
                            </div>
                        )
                        : reviewReport && (
                            <>
                                {/* Overall quality */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                                    <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>Overall Quality:</span>
                                    <span style={{
                                        fontWeight: 700,
                                        color: qualityColor(reviewReport.overall_quality),
                                        textTransform: 'capitalize',
                                        fontSize: '0.85rem'
                                    }}>
                                        {reviewReport.overall_quality}
                                    </span>
                                </div>

                                {/* Summary */}
                                <p style={{ fontSize: '0.87rem', color: '#374151', marginBottom: 16, lineHeight: 1.6 }}>
                                    {reviewReport.summary}
                                </p>

                                {/* Issues list */}
                                {reviewReport.issues && reviewReport.issues.length > 0 && (
                                    <div>
                                        <p style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 8 }}>
                                            Flagged Items ({reviewReport.issues.length})
                                        </p>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {reviewReport.issues.map((issue, idx) => (
                                                <div
                                                    key={idx}
                                                    style={{
                                                        display: 'flex',
                                                        gap: 10,
                                                        alignItems: 'flex-start',
                                                        padding: '8px 12px',
                                                        background: '#f9fafb',
                                                        borderRadius: 6,
                                                        borderLeft: `3px solid ${issue.severity === 'high' ? '#dc3545' : issue.severity === 'medium' ? '#fd7e14' : '#6c757d'}`
                                                    }}
                                                >
                                                    {severityIcon(issue.severity)}
                                                    <div style={{ flex: 1 }}>
                                                        <span style={{
                                                            fontSize: '0.72rem',
                                                            fontWeight: 700,
                                                            textTransform: 'uppercase',
                                                            color: '#9ca3af',
                                                            marginRight: 8
                                                        }}>
                                                            {issue.type}
                                                        </span>
                                                        <span style={{ fontSize: '0.84rem', color: '#374151' }}>
                                                            {issue.description}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {reviewReport.issues && reviewReport.issues.length === 0 && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#198754', fontSize: '0.87rem' }}>
                                        <CheckCircleFill size={15} />
                                        No issues found. Your requirements look good!
                                    </div>
                                )}

                                {/* Per document analysis (documents source) */}
                                {reviewReport.doc_item_notes && reviewReport.doc_item_notes.length > 0 && (
                                    <div style={{ marginTop: 20 }}>
                                        <p style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 12 }}>
                                            Per Document Analysis
                                        </p>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            {reviewReport.doc_item_notes.map((doc, idx) => (
                                                <div
                                                    key={idx}
                                                    style={{
                                                        padding: '10px 14px',
                                                        background: '#f9fafb',
                                                        borderRadius: 8,
                                                        border: '1px solid #e8ecf0',
                                                    }}
                                                >
                                                    <div style={{ fontWeight: 700, fontSize: '0.83rem', color: '#374151', marginBottom: doc.note ? 6 : 0 }}>
                                                        {doc.doc_title}
                                                    </div>
                                                    {doc.note
                                                        ? <p style={{ fontSize: '0.83rem', color: '#5f6f82', margin: 0, lineHeight: 1.6 }}>{doc.note}</p>
                                                        : <p style={{ fontSize: '0.83rem', color: '#9ca3af', margin: 0, fontStyle: 'italic' }}>No issues found for this document.</p>
                                                    }
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Per work item analysis (ADO source) */}
                                {reviewReport.ado_item_notes && reviewReport.ado_item_notes.length > 0 && (
                                    <div style={{ marginTop: 20 }}>
                                        <p style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 12 }}>
                                            Per Work Item Analysis
                                        </p>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            {reviewReport.ado_item_notes.map((item) => (
                                                <div
                                                    key={item.work_item_id}
                                                    style={{
                                                        padding: '10px 14px',
                                                        background: '#f9fafb',
                                                        borderRadius: 8,
                                                        border: '1px solid #e8ecf0',
                                                    }}
                                                >
                                                    <div style={{ fontWeight: 700, fontSize: '0.83rem', color: '#374151', marginBottom: item.note ? 6 : 0 }}>
                                                        #{item.work_item_id} — {item.work_item_title}
                                                    </div>
                                                    {item.note
                                                        ? <p style={{ fontSize: '0.83rem', color: '#5f6f82', margin: 0, lineHeight: 1.6 }}>{item.note}</p>
                                                        : <p style={{ fontSize: '0.83rem', color: '#9ca3af', margin: 0, fontStyle: 'italic' }}>No issues found for this item.</p>
                                                    }
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )
                    }

                    {/* Additional requirement context — steers test-case generation for this run */}
                    {!reviewLoading && reviewReport && (
                        <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #e8ecf0' }}>
                            <label
                                htmlFor="requirements-additional-context"
                                style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', marginBottom: 6, color: '#374151' }}
                            >
                                Additional Requirement Context <span style={{ fontWeight: 500, color: '#9ca3af' }}>(optional)</span>
                            </label>
                            <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: 8, lineHeight: 1.5 }}>
                                Add any extra requirements, business rules, or edge cases you want covered. This is used
                                together with the uploaded document(s) to generate the test cases.
                            </p>
                            <Form.Control
                                id="requirements-additional-context"
                                as="textarea"
                                rows={4}
                                placeholder="e.g. Cover session-timeout after 15 minutes of inactivity; validate that admin users bypass the approval step…"
                                value={additionalContext}
                                onChange={(e) => setAdditionalContext(e.target.value)}
                                disabled={queuingJob}
                                style={{ fontSize: '0.85rem' }}
                            />
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer style={{ position: 'relative' }}>
                    {/* Copied! toast — shown for both flows */}
                    {copySuccess && (
                        <span style={{
                            position: 'absolute', left: 16,
                            display: 'flex', alignItems: 'center', gap: 5,
                            fontSize: '0.82rem', color: '#198754', fontWeight: 600,
                        }}>
                            <CheckCircleFill size={13} />
                            Copied to clipboard!
                        </span>
                    )}
                    {/* Copy to Clipboard — both flows, visible when report is loaded */}
                    {reviewReport && !reviewLoading && (
                        <button
                            className="rp-btn-secondary"
                            type="button"
                            onClick={handleCopyAnalysis}
                        >
                            Copy to Clipboard
                        </button>
                    )}
                    {/* Add Notes to ADO — ADO source only when per-item notes present */}
                    {dataSource === 'ado' && reviewReport?.ado_item_notes && reviewReport.ado_item_notes.length > 0 && !reviewLoading && (
                        <button
                            className="rp-btn-secondary"
                            type="button"
                            onClick={handleOpenAdoNotes}
                        >
                            Add Notes to ADO
                        </button>
                    )}
                    <button
                        id="requirements-review-confirm-generate"
                        className="rp-generate-btn"
                        type="button"
                        onClick={handleConfirmGenerate}
                        disabled={reviewLoading || queuingJob || hasActiveTestCaseGenerationJob}
                    >
                        {queuingJob
                            ? (<><Spinner animation="border" size="sm" style={{ marginRight: 7 }} />Queuing…</>)
                            : hasActiveTestCaseGenerationJob
                            ? (<><Spinner animation="border" size="sm" style={{ marginRight: 7 }} />Generation Running</>)
                            : (<><Lightning size={14} style={{ marginRight: 5 }} />Generate Test Cases</>)
                        }
                    </button>
                </Modal.Footer>
            </Modal>

            {/* ---- ADO notes preview/edit modal ---- */}
            <Modal show={showAdoNotesModal} onHide={() => { setShowAdoNotesModal(false); setAdoNotesResult(null); }} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '0.95rem', fontWeight: 700 }}>
                        Add Review Notes to ADO Work Items
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                    {adoNotesResult
                        ? (
                            /* Results view */
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <p style={{ fontSize: '0.87rem', color: '#374151', marginBottom: 8 }}>Results:</p>
                                {adoNotesResult.map((r) => (
                                    <div key={r.work_item_id} style={{
                                        display: 'flex', alignItems: 'center', gap: 8,
                                        padding: '6px 10px', borderRadius: 6,
                                        background: r.status === 'ok' ? '#f0fdf4' : r.status === 'skipped' ? '#f9fafb' : '#fff1f2',
                                        fontSize: '0.84rem'
                                    }}>
                                        {r.status === 'ok' && <CheckCircleFill size={13} color="#198754" />}
                                        {r.status === 'skipped' && <span style={{ color: '#9ca3af' }}>—</span>}
                                        {r.status === 'error' && <ExclamationCircleFill size={13} color="#dc3545" />}
                                        <span>
                                            {r.work_item_id > 0 ? `#${r.work_item_id}` : ''}
                                            {' '}{r.status === 'ok' ? 'Comment posted' : r.status === 'skipped' ? 'Skipped (no note)' : `Error: ${r.detail}`}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )
                        : (
                            /* Edit view */
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                <p style={{ fontSize: '0.83rem', color: '#9ca3af', margin: 0 }}>
                                    Edit notes below. Items with empty notes will be skipped.
                                </p>
                                {adoNotesDraft.map((item, idx) => (
                                    <div key={item.work_item_id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        <label style={{ fontSize: '0.83rem', fontWeight: 600, color: '#374151' }}>
                                            #{item.work_item_id} — {item.work_item_title}
                                        </label>
                                        <textarea
                                            rows={3}
                                            value={item.note}
                                            onChange={(e) => {
                                                const updated = [...adoNotesDraft];
                                                updated[idx] = { ...updated[idx], note: e.target.value };
                                                setAdoNotesDraft(updated);
                                            }}
                                            placeholder="Leave empty to skip this work item"
                                            style={{
                                                width: '100%', padding: '8px 10px', fontSize: '0.83rem',
                                                border: '1px solid #d1d5db', borderRadius: 6,
                                                resize: 'vertical', outline: 'none', color: '#374151',
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        )
                    }
                </Modal.Body>
                <Modal.Footer>
                    <button className="rp-btn-secondary" type="button" onClick={() => { setShowAdoNotesModal(false); setAdoNotesResult(null); }}>
                        {adoNotesResult ? 'Close' : 'Cancel'}
                    </button>
                    {!adoNotesResult && (
                        <button
                            className="rp-btn-primary"
                            type="button"
                            disabled={adoNotesPosting}
                            onClick={handlePostAdoNotes}
                        >
                            {adoNotesPosting
                                ? (<><Spinner animation="border" size="sm" style={{ marginRight: 6 }} />Posting…</>)
                                : 'Confirm & Post'
                            }
                        </button>
                    )}
                </Modal.Footer>
            </Modal>
            </>
        );
    }

    function renderGenerationCompletionModal(): React.ReactNode
    {
        const job = completedGenerationJob;
        const hasTests = job ? jobHasTestCases(job) : false;
        const versionLabel = job?.version_number != null ? `v${job.version_number}` : job ? `#${job.id}` : '';

        return (
            <Modal
                show={job != null}
                onHide={() => setCompletedGenerationJob(null)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '0.95rem', fontWeight: 700 }}>
                        Generation Complete
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ marginBottom: 8, color: '#374151', lineHeight: 1.6 }}>
                        {`Generation ${versionLabel} completed.`}
                    </p>
                    <p style={{ margin: 0, color: '#5f6f82', lineHeight: 1.6 }}>
                        {hasTests
                            ? 'Would you like to open the Test Cases tab now, or stay on this tab?'
                            : 'Test cases are not available for this generation yet. You can stay on this tab and review the generation history.'}
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        className="rp-btn-secondary"
                        type="button"
                        onClick={() => setCompletedGenerationJob(null)}
                    >
                        Stay Here
                    </button>
                    <button
                        className="rp-btn-primary"
                        type="button"
                        disabled={!hasTests}
                        onClick={() =>
                        {
                            if (!job) return;
                            setCompletedGenerationJob(null);
                            handleViewResults(job);
                        }}
                    >
                        View Test Cases
                    </button>
                </Modal.Footer>
            </Modal>
        );
    }

    /* ---- Section 1: Upload Documents ---- */

    function renderUploadSection(): React.ReactNode
    {
        // Only render when dataSource === 'documents' (or for non-Azure projects)
        if (isAzureProject && dataSource !== 'documents') return null;
        const dropZoneClass = [
            'rp-drop-zone',
            isDragOver ? 'rp-drop-zone--active' : '',
            uploading || loadingDocs ? 'rp-drop-zone--disabled' : ''
        ].filter(Boolean).join(' ');

        return (
            <div id="requirements-upload-section" className="rp-card">
                <div className="rp-card-header">
                    <div>
                        <p className="rp-card-title">Upload Documents</p>
                        <p className="rp-card-subtitle">Add requirement documents to use for test generation.</p>
                    </div>
                </div>

                <form onSubmit={handleUploadSubmit} noValidate>
                    {/* Drop zone */}
                    <div
                        id="requirements-upload-dropzone"
                        className={dropZoneClass}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={uploading || loadingDocs ? undefined : handleDropZoneClick}
                        role="button"
                        tabIndex={0}
                        aria-label="Drop files here or click to browse"
                        onKeyDown={(e) =>
                        {
                            if (e.key === 'Enter' || e.key === ' ') handleDropZoneClick();
                        }}
                    >
                        <CloudUploadFill size={28} className="rp-drop-zone-icon" />
                        <p className="rp-drop-zone-label">
                            Drop files here or <span className="rp-drop-zone-browse">browse</span>
                        </p>
                        <p className="rp-drop-zone-hint">PDF · DOCX · TXT · Markdown · CSV · XLSX</p>
                    </div>

                    <input
                        ref={fileInputRef}
                        className="rp-file-input"
                        type="file"
                        multiple
                        accept={ACCEPTED_TYPES}
                        onChange={handleFileInputChange}
                        disabled={uploading || loadingDocs}
                    />

                    {/* Selected files — per-file classification + context description */}
                    {selectedFiles.length > 0 && (
                        <div id="requirements-upload-selected-files" style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
                            {selectedFiles.map((file, index) =>
                            {
                                const fileCls = fileClassifications[file.name] || '';
                                const templates = DESCRIPTION_TEMPLATES[fileCls] ?? [];
                                const isFirstSelectedFile = index === 0;
                                return (
                                    <div
                                        key={file.name}
                                        id={isFirstSelectedFile ? 'requirements-upload-file-0' : undefined}
                                        style={{
                                            border: '1px solid #e8ecf0',
                                            borderRadius: 8,
                                            padding: '12px 14px',
                                            background: '#f9fafb',
                                        }}
                                    >
                                        {/* File header row */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                            <FileEarmarkText size={14} color="#6c757d" style={{ flexShrink: 0 }} />
                                            <span style={{ fontSize: '0.84rem', fontWeight: 600, color: '#374151', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {file.name}
                                            </span>
                                            <span style={{ fontSize: '0.75rem', color: '#9ca3af', flexShrink: 0 }}>
                                                {formatFileSize(file.size)}
                                            </span>
                                            <button
                                                className="rp-file-pill-remove"
                                                type="button"
                                                onClick={() => handleRemoveFile(index)}
                                                aria-label={`Remove ${file.name}`}
                                                style={{ flexShrink: 0 }}
                                            >
                                                <XLg size={10} />
                                            </button>
                                        </div>

                                        {/* Per-file classification */}
                                        <div id={isFirstSelectedFile ? 'requirements-upload-file-classification' : undefined} style={{ marginBottom: 10 }}>
                                            <label
                                                htmlFor={`file-cls-${index}`}
                                                style={{ fontSize: '0.78rem', fontWeight: 600, color: '#5f6f82', display: 'block', marginBottom: 4 }}
                                            >
                                                Classification
                                                <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 4 }}>(required)</span>
                                            </label>
                                            <Form.Select
                                                id={`file-cls-${index}`}
                                                size="sm"
                                                value={fileCls}
                                                onChange={(e) =>
                                                {
                                                    const val = e.target.value as DocumentClassification | '';
                                                    setFileClassifications((prev) => ({ ...prev, [file.name]: val }));
                                                    // Clear the description when classification changes
                                                    setFileNotes((prev) => ({ ...prev, [file.name]: '' }));
                                                }}
                                                disabled={uploading}
                                                style={{ maxWidth: 280, fontSize: '0.84rem' }}
                                            >
                                                <option value="">Select classification…</option>
                                                <option value="new_feature">New Functionality</option>
                                                <option value="update_existing">Existing Functionality Change</option>
                                                <option value="something_else">Something Else</option>
                                            </Form.Select>
                                        </div>

                                        {/* Template chips — shown once classification is selected */}
                                        {fileCls && templates.length > 0 && (
                                            <div style={{ marginBottom: 8 }}>
                                                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#5f6f82', display: 'block', marginBottom: 5 }}>
                                                    Suggested descriptions — click one to use it:
                                                </span>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                    {templates.map((tpl) => (
                                                        <button
                                                            key={tpl}
                                                            type="button"
                                                            onClick={() => setFileNotes((prev) => ({ ...prev, [file.name]: tpl }))}
                                                            disabled={uploading}
                                                            style={{
                                                                textAlign: 'left',
                                                                background: fileNotes[file.name] === tpl ? '#eef4ff' : '#fff',
                                                                border: fileNotes[file.name] === tpl ? '1px solid #93b8fd' : '1px solid #e0e4ea',
                                                                borderRadius: 6,
                                                                padding: '5px 10px',
                                                                fontSize: '0.79rem',
                                                                color: fileNotes[file.name] === tpl ? '#1a56db' : '#4b5563',
                                                                cursor: uploading ? 'default' : 'pointer',
                                                                lineHeight: 1.4,
                                                            }}
                                                        >
                                                            {tpl}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Context description textarea */}
                                        {fileCls && (
                                            <div id={isFirstSelectedFile ? 'requirements-upload-file-context' : undefined}>
                                                <label
                                                    htmlFor={`file-notes-${index}`}
                                                    style={{ fontSize: '0.78rem', fontWeight: 600, color: '#5f6f82', display: 'block', marginBottom: 4 }}
                                                >
                                                    Context description
                                                    <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 4 }}>(required — edit or write your own)</span>
                                                </label>
                                                <textarea
                                                    id={`file-notes-${index}`}
                                                    rows={2}
                                                    value={fileNotes[file.name] || ''}
                                                    onChange={(e) => setFileNotes((prev) => ({ ...prev, [file.name]: e.target.value }))}
                                                    placeholder={fileCls === 'something_else' ? 'Describe what this document contains and why it is relevant…' : 'Edit the suggested description above, or write your own…'}
                                                    disabled={uploading}
                                                    style={{
                                                        width: '100%',
                                                        padding: '6px 10px',
                                                        fontSize: '0.82rem',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: 6,
                                                        resize: 'vertical',
                                                        outline: 'none',
                                                        color: '#374151',
                                                        background: uploading ? '#f3f4f6' : '#fff',
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Upload error */}
                    {uploadError !== null && (
                        <Alert
                            variant="danger"
                            className="rp-alert mb-3"
                            onClose={() => setUploadError(null)}
                            dismissible
                        >
                            {uploadError}
                        </Alert>
                    )}

                    {/* Submit row */}
                    <div className="rp-upload-footer">
                        <button
                            id="requirements-upload-submit"
                            className="rp-btn-primary"
                            type="submit"
                            disabled={uploading || loadingDocs || selectedFiles.length === 0}
                        >
                            {uploading
                                ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="rp-btn-spinner" />
                                        Uploading…
                                    </>
                                )
                                : (
                                    <>
                                        <CloudUploadFill size={14} />
                                        Upload
                                    </>
                                )
                            }
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    /* ---- Section 2: Documents list ---- */

    function renderDocumentsSection(): React.ReactNode
    {
        // Only render when dataSource === 'documents' (or for non-Azure projects)
        if (isAzureProject && dataSource !== 'documents') return null;
        return (
            <div id="requirements-document-selection" className="rp-card">
                <div className="rp-card-header">
                    <div>
                        <p className="rp-card-title">
                            Documents
                            {documents.length > 0 && (
                                <span
                                    style={{
                                        marginLeft: 8,
                                        background: '#eef4ff',
                                        color: '#0d6efd',
                                        borderRadius: 12,
                                        padding: '1px 10px',
                                        fontSize: '0.75rem',
                                        fontWeight: 700
                                    }}
                                >
                                    {documents.length}
                                </span>
                            )}
                        </p>
                        <p className="rp-card-subtitle">Uploaded requirement sources for test generation.</p>
                    </div>
                    {loadingDocs && <Spinner animation="border" size="sm" />}
                </div>

                {documents.length === 0
                    ? (
                        <div className="rp-history-empty">
                            No documents uploaded yet. Upload PDF or DOCX files to get started.
                        </div>
                    )
                    : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {readyDocuments.length > 0 && (
                            <div style={{ display: 'flex', gap: 12, marginBottom: 4, fontSize: '0.8rem' }}>
                                <button
                                    type="button"
                                    style={{ background: 'none', border: 'none', color: '#0d6efd', cursor: 'pointer', padding: 0 }}
                                    onClick={() =>
                                    {
                                        const allReadyIds = readyDocuments.map((d) => d.id);
                                        const allSelected = allReadyIds.every((id) => selectedDocIds.includes(id));
                                        setSelectedDocIds(allSelected ? [] : allReadyIds);
                                    }}
                                >
                                    {readyDocuments.every((d) => selectedDocIds.includes(d.id)) ? 'Deselect All' : 'Select All'}
                                </button>
                                <span style={{ color: '#9ca3af' }}>
                                    {selectedDocIds.length}/{readyDocuments.length} selected
                                </span>
                            </div>
                            )}
                            {documents.map((doc) => (
                                <div
                                    key={doc.id}
                                    id={readyDocuments[0]?.id === doc.id ? 'requirements-document-selection-first-ready' : undefined}
                                    className="rp-doc-row"
                                    style={{ cursor: doc.status === 'ready' ? 'pointer' : undefined }}
                                    onClick={() =>
                                    {
                                        if (doc.status !== 'ready') return;
                                        setSelectedDocIds((prev) =>
                                            prev.includes(doc.id)
                                                ? prev.filter((id) => id !== doc.id)
                                                : [...prev, doc.id]
                                        );
                                    }}
                                >
                                    {doc.status === 'ready'
                                        ? (
                                            <Form.Check
                                                type="checkbox"
                                                id={`doc-sel-${doc.id}`}
                                                checked={selectedDocIds.includes(doc.id)}
                                                onChange={(e) =>
                                                {
                                                    e.stopPropagation();
                                                    setSelectedDocIds((prev) =>
                                                        prev.includes(doc.id)
                                                            ? prev.filter((id) => id !== doc.id)
                                                            : [...prev, doc.id]
                                                    );
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                style={{ margin: 0, flexShrink: 0 }}
                                            />
                                        )
                                        : <span style={{ width: 16, flexShrink: 0 }} />}

                                    <FileEarmarkText size={16} color="#9ca3af" style={{ flexShrink: 0 }} />

                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div className="rp-doc-name" title={doc.original_filename}>
                                            {doc.original_filename}
                                        </div>
                                        {doc.notes && (
                                            <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={doc.notes}>
                                                {doc.notes}
                                            </div>
                                        )}
                                    </div>

                                    <span className="rp-doc-meta">{formatFileSize(doc.file_size)}</span>

                                    {renderClassificationBadge(doc.classification)}

                                    <span className={`rp-badge ${getDocStatusClass(doc.status)}`}>
                                        {doc.status}
                                    </span>

                                    <div className="rp-doc-actions">
                                        <button
                                            className="rp-doc-icon-btn"
                                            type="button"
                                            title="Download"
                                            onClick={() => handleDownloadDocument(doc)}
                                            disabled={uploading || loadingDocs}
                                        >
                                            <Download size={14} />
                                        </button>

                                        <button
                                            className="rp-doc-icon-btn"
                                            type="button"
                                            title="Reprocess"
                                            onClick={() => handleReprocessDocument(doc)}
                                            disabled={uploading || loadingDocs || doc.status === 'extracting'}
                                        >
                                            <ArrowRepeat size={14} />
                                        </button>

                                        <button
                                            className="rp-doc-icon-btn rp-doc-icon-btn--danger"
                                            type="button"
                                            title="Delete"
                                            onClick={() => handleDeleteDocument(doc)}
                                            disabled={uploading || loadingDocs}
                                        >
                                            <Trash3 size={14} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                }
            </div>
        );
    }

    /* ---- Section 3: ADO Work Items (conditional) ---- */

    function renderAdoSection(): React.ReactNode
    {
        if (!isAzureProject) return null;
        // Only render when dataSource === 'ado'
        if (dataSource !== 'ado') return null;

        const adoTypeClass = (type: string): string =>
        {
            if (type === 'Epic') return 'rp-ado-type-badge rp-ado-type-badge--epic';
            if (type === 'User Story') return 'rp-ado-type-badge rp-ado-type-badge--story';
            if (type === 'Bug') return 'rp-ado-type-badge rp-ado-type-badge--bug';
            return 'rp-ado-type-badge';
        };

        const allSelected = adoWorkItems.length > 0 && selectedAdoIds.length === adoWorkItems.length;

        return (
            <div className="rp-card">
                <div className="rp-card-header">
                    <div>
                        <p className="rp-card-title">Azure DevOps Work Items</p>
                        <p className="rp-card-subtitle">
                            Sync supported Azure DevOps work items to use as requirements.
                        </p>
                    </div>

                    <button
                        className="rp-btn-secondary"
                        type="button"
                        onClick={() => { setShowAdoTypeModal(true); }}
                        disabled={adoSyncing}
                    >
                        {adoSyncing
                            ? (<><Spinner animation="border" size="sm" className="rp-btn-spinner" />Syncing…</>)
                            : (<><ArrowRepeat size={14} />Sync from ADO</>)
                        }
                    </button>
                </div>

                <p style={{ fontSize: '0.78rem', color: '#9ca3af', margin: '0 0 12px 0' }}>
                    {adoLastSynced
                        ? `Last synced: ${formatLocalDateTimeFromUTC(adoLastSynced)}`
                        : 'Not yet synced'
                    }
                </p>

                {adoError !== null && (
                    <Alert variant="danger" className="rp-alert mb-3" onClose={() => setAdoError(null)} dismissible>
                        {adoError}
                    </Alert>
                )}

                {adoWorkItems.length > 0 && (
                    <>
                        <div style={{ display: 'flex', gap: 12, marginBottom: 6, fontSize: '0.8rem' }}>
                            <button
                                type="button"
                                style={{ background: 'none', border: 'none', color: '#0d6efd', cursor: 'pointer', padding: 0 }}
                                onClick={allSelected ? handleDeselectAllAdo : handleSelectAllAdo}
                            >
                                {allSelected ? 'Deselect All' : 'Select All'}
                            </button>
                            <span style={{ color: '#9ca3af' }}>
                                {selectedAdoIds.length}/{adoWorkItems.length} selected
                            </span>
                        </div>

                        <div className="rp-ado-list">
                            {adoWorkItems.map((item) => (
                                <div
                                    key={item.id}
                                    className="rp-ado-item"
                                    onClick={() => handleToggleAdoItem(item.id)}
                                >
                                    <Form.Check
                                        type="checkbox"
                                        id={`ado-item-${item.id}`}
                                        checked={selectedAdoIds.includes(item.id)}
                                        onChange={() => handleToggleAdoItem(item.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ margin: 0 }}
                                    />
                                    <span className={adoTypeClass(item.type)}>{item.type}</span>
                                    <span className="rp-ado-item-title" title={item.title}>
                                        {item.title}
                                    </span>
                                    <span className="rp-ado-item-state">{item.state}</span>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        );
    }

    /* ---- Section 4: Generate Test Cases action bar ---- */

    function renderGenerateBar(): React.ReactNode
    {
        const docCount = dataSource === 'documents' ? selectedDocIds.length : 0;
        const adoCount = dataSource === 'ado' ? selectedAdoIds.length : 0;
        const hasInput = docCount > 0 || adoCount > 0;

        let infoText: string;
        if (hasActiveTestCaseGenerationJob)
        {
            infoText = 'A test case generation job is already running for this project.';
        }
        else if (dataSource === 'ado')
        {
            infoText = adoCount > 0
                ? `Generate test cases from ${adoCount} selected ADO work item${adoCount !== 1 ? 's' : ''}.`
                : 'Sync and select ADO work items to enable generation.';
        }
        else
        {
            infoText = docCount > 0
                ? `Generate test cases from ${docCount} selected document${docCount !== 1 ? 's' : ''}.`
                : 'Upload documents and select at least one to enable generation.';
        }

        return (
            <div id="requirements-generate-action" className="rp-generate-bar">
                <p className="rp-generate-bar-info">{infoText}</p>

                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <button
                        id="requirements-generate-button"
                        className="rp-generate-btn"
                        type="button"
                        onClick={handleGenerate}
                        disabled={!hasInput || hasActiveTestCaseGenerationJob || queuingJob || reviewLoading || uploading}
                    >
                        {reviewLoading
                            ? (
                                <>
                                    <Spinner animation="border" size="sm" style={{ marginRight: 7 }} />
                                    Analysing…
                                </>
                            )
                            : queuingJob
                            ? (
                                <>
                                    <Spinner animation="border" size="sm" style={{ marginRight: 7 }} />
                                    Queuing…
                                </>
                            )
                            : hasActiveTestCaseGenerationJob
                            ? (
                                <>
                                    <Spinner animation="border" size="sm" style={{ marginRight: 7 }} />
                                    Generation Running
                                </>
                            )
                            : (
                                <>
                                    <Lightning size={15} />
                                    Generate Test Cases
                                </>
                            )
                        }
                    </button>
                </div>
            </div>
        );
    }

    /* ---- Section 5: Generation History ---- */

    function renderGenerationHistory(): React.ReactNode
    {
        const displayedJobs = showAllHistory ? jobs : jobs.slice(0, MAX_HISTORY_DEFAULT);

        return (
            <div className="rp-card">
                <div className="rp-card-header">
                    <p className="rp-card-title">Generation History</p>
                    {loadingJobs && <Spinner animation="border" size="sm" />}
                </div>

                {jobs.length === 0
                    ? (
                        <div className="rp-history-empty">
                            No generations yet. Upload documents and click Generate Test Cases to get started.
                        </div>
                    )
                    : (
                        <>
                            <div className="rp-history-list">
                                {displayedJobs.map((job) =>
                                {
                                    const vn = (job as any).version_number;
                                    const versionLabel = vn != null ? `v${vn}` : '—';
                                    const badgeClass = `rp-job-badge ${getJobBadgeClass(job.status)}`;
                                    const isRunning = isActiveGenerationStatus(job.status);
                                    const liveTestCount = job.live_results?.test_case_count;
                                    const testCount = job.artifact_counts?.test_cases ?? (liveTestCount != null ? liveTestCount : null);
                                    const sourceDocs = job.source_documents ?? [];
                                    const isSyntheticJob = normalizeJobType((job as any).job_type || job.generation_kind) === 'synthetic_data';

                                    return (
                                        <div key={job.id} className="rp-history-row-wrapper">
                                            <div className="rp-history-row">
                                            <span className="rp-history-version">{versionLabel}</span>

                                            {/* data_source_type badge — always render to preserve grid column alignment */}
                                            <span style={{
                                                fontSize: '0.70rem',
                                                fontWeight: 700,
                                                padding: (isSyntheticJob || job.data_source_type) ? '1px 7px' : '0',
                                                borderRadius: 10,
                                                background: isSyntheticJob ? '#f3f0ff' : job.data_source_type === 'ado' ? '#e0f0ff' : job.data_source_type ? '#f0fdf4' : 'transparent',
                                                color: isSyntheticJob ? '#6b21a8' : job.data_source_type === 'ado' ? '#0369a1' : '#166534',
                                                textTransform: 'uppercase',
                                            }}>
                                                {isSyntheticJob ? 'Synthetic' : job.data_source_type === 'ado' ? 'ADO' : job.data_source_type ? 'Docs' : ''}
                                            </span>

                                            <span>
                                                {isRunning && <span className="rp-history-running-dot" />}
                                                <span className={badgeClass}>{job.status}</span>
                                            </span>

                                            <span style={{ color: '#5f6f82', fontSize: '0.82rem' }}>
                                                {testCount != null ? `${testCount} tests` : '—'}
                                            </span>

                                            <span style={{ color: '#5f6f82', fontSize: '0.82rem' }}>
                                                {job.document_count > 0
                                                    ? `${job.document_count} doc${job.document_count !== 1 ? 's' : ''}`
                                                    : '—'
                                                }
                                            </span>

                                            <span style={{ color: '#9ca3af', fontSize: '0.78rem' }}>
                                                {formatLocalDateTimeFromUTC(job.created_at)}
                                            </span>

                                            <div style={{ display: 'flex', gap: 6 }}>
                                                {canViewLogs && (
                                                    <button
                                                        className="rp-btn-secondary"
                                                        type="button"
                                                        style={{ fontSize: '0.78rem', padding: '4px 10px' }}
                                                        onClick={() => handleViewLogs(job)}
                                                    >
                                                        View Logs
                                                    </button>
                                                )}

                                                {isSyntheticJob && (
                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        <button
                                                            className="rp-btn-secondary"
                                                            type="button"
                                                            style={{ fontSize: '0.78rem', padding: '4px 8px', color: job.status.toLowerCase() === 'completed' ? '#6b21a8' : '#94a3b8', borderColor: job.status.toLowerCase() === 'completed' ? '#d8b4fe' : '#e2e8f0' }}
                                                            onClick={() => handleViewSyntheticData(job)}
                                                            disabled={job.status.toLowerCase() !== 'completed'}
                                                            title="View Data"
                                                        >
                                                            {loadingDataset ? <Spinner animation="border" size="sm" /> : <Eye size={14} />}
                                                            <span style={{ marginLeft: '4px' }}>View Data</span>
                                                        </button>
                                                    </div>
                                                )}

                                                {job.analysis_report && (
                                                    <button
                                                        className="rp-btn-secondary"
                                                        type="button"
                                                        style={{ fontSize: '0.78rem', padding: '4px 10px' }}
                                                        onClick={() => setAnalysisReportJob(job)}
                                                    >
                                                        View Analysis Report
                                                    </button>
                                                )}

                                                {(job.additional_context || '').trim() && (
                                                    <button
                                                        className="rp-btn-secondary"
                                                        type="button"
                                                        style={{ fontSize: '0.78rem', padding: '4px 10px' }}
                                                        onClick={() => setAdditionalContextJob(job)}
                                                    >
                                                        View Context
                                                    </button>
                                                )}

                                                {jobHasTestCases(job) && (
                                                    <button
                                                        className="rp-btn-primary"
                                                        type="button"
                                                        style={{ fontSize: '0.78rem', padding: '4px 10px' }}
                                                        onClick={() => handleViewResults(job)}
                                                    >
                                                        View Tests
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                            {sourceDocs.length > 0 && (
                                                <div className="rp-history-source-docs">
                                                    <span className="rp-history-source-label">Sources:</span>
                                                    {sourceDocs.map((doc) => (
                                                        <span key={doc.id} className="rp-history-source-chip">
                                                            <span className="rp-history-source-filename">{doc.filename}</span>
                                                            {doc.classification && (
                                                                <span className={`rp-history-source-cls ${
                                                                    doc.classification === 'new_feature' ? 'rp-cls-new'
                                                                    : doc.classification === 'update_existing' ? 'rp-cls-update'
                                                                    : 'rp-cls-other'
                                                                }`}>
                                                                    {doc.classification === 'new_feature' ? 'New Functionality'
                                                                    : doc.classification === 'update_existing' ? 'Existing Functionality Change'
                                                                    : 'Something Else'}
                                                                </span>
                                                            )}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}

                                            {isRunning && renderLiveGenerationResults(job)}
                                        </div>
                                    );
                                })}
                            </div>

                            {jobs.length > MAX_HISTORY_DEFAULT && (
                                <div style={{ marginTop: 12, textAlign: 'center' }}>
                                    <button
                                        type="button"
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#0d6efd',
                                            cursor: 'pointer',
                                            fontSize: '0.85rem'
                                        }}
                                        onClick={() => setShowAllHistory((prev) => !prev)}
                                    >
                                        {showAllHistory
                                            ? 'Show less'
                                            : `Show all ${jobs.length} generations`
                                        }
                                    </button>
                                </div>
                            )}
                        </>
                    )
                }
            </div>
        );
    }

    /* ---- Logs modal ---- */

    function renderLogsModal(): React.ReactNode
    {
        return (
            <StreamingJobLogsModal
                show={logsModal.show}
                title={logsModal.title}
                jobId={logsModal.jobId}
                initialStatus={logsModal.status}
                downloadName="workflow.log"
                onHide={handleCloseLogsModal}
            />
        );
    }

    /* ---------- Main render ---------- */

    return (
        <div className="rp-panel">
            {/* Panel-level feedback message */}
            {panelMessage !== null && (
                <Alert
                    variant={panelMessage.type}
                    className="rp-alert mb-0"
                    onClose={() => setPanelMessage(null)}
                    dismissible
                >
                    {panelMessage.text}
                </Alert>
            )}

            {/* Source toggle bar (Azure projects only) */}
            {renderSourceToggleBar()}

            {/* 1. Upload Documents (shown when dataSource='documents' or non-Azure) */}
            {renderUploadSection()}

            {/* 2. Documents list (shown when dataSource='documents' or non-Azure) */}
            {renderDocumentsSection()}

            {/* 3. ADO Work Items (shown when dataSource='ado' and Azure project) */}
            {renderAdoSection()}

            {/* 4. Generate Test Cases action bar */}
            {renderGenerateBar()}

            {/* 5. Generation History */}
            {renderGenerationHistory()}

            {/* Source selector modal */}
            {renderSourceModal()}

            {/* ADO freshness modal */}
            {renderAdoFreshnessModal()}

            {/* Unchanged document warning modal */}
            {renderUnchangedGenerateWarningModal()}

            {/* Review modal */}
            {renderReviewModal()}

            {/* Saved analysis report modal */}
            {renderSavedAnalysisReportModal()}
            {renderAdditionalContextModal()}

            {/* Generation completion modal */}
            {renderGenerationCompletionModal()}

            {/* Logs modal */}
            {renderLogsModal()}

            {/* Synthetic Data Preview Modal */}
            {previewDataset !== null && (
                <TestDatasetPreviewModal
                    dataset={previewDataset}
                    onClose={() => setPreviewDataset(null)}
                    onDownload={(datasetId) => handleDownloadSyntheticCSV(undefined, datasetId)}
                />
            )}
        </div>
    );
}

/* ---------- Export ---------- */

export default ProjectRequirementsPanelNew;
